import React from 'react';
import { TableCell, TableRow, Typography } from '@mui/material';
import { ComparisonEventsListRowDetailField } from './ComparisonEventsListRowDetailField';
import {
  type ComparisonItem,
  type ComparisonItemSourceFields,
  ComparisonItemType,
} from '../../../../../types/BaselineMeasurements';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDialog, useSnakeBar } from '../../../../../hooks';
import { TranslationKeys } from '../../../../../translations';
import { ConfirmDialog } from '../../../../../components';
import ActionButton from '../../../../../components/ActionButton';
import type { Connection } from '../../../../../types';
import { useCreateEventFromDifferenceMutation } from '../../../../../hooks/api/baseline-measurements';

type ComparisonEventsListRowDetailRowProps = {
  connectionId: Connection['connectionId'] | undefined;
  item: ComparisonItem;
  field: keyof ComparisonItemSourceFields;
};

export const ComparisonEventsListRowDetailRow = ({
  connectionId,
  item,
  field,
}: ComparisonEventsListRowDetailRowProps) => {
  const intl = useIntl();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();
  const {
    dialogState: showConfirmCreateEventDialog,
    openDialog: openConfirmCreateEventDialog,
    closeDialog: closeConfirmCreateEventDialog,
  } = useDialog();
  const { eventSource, insurerSource } = item;
  const discrepanciesFields = item.discrepancies.map(d => d.field);
  const isDifferent = discrepanciesFields.includes(field);
  const comparisonId = item.comparisonId;
  const comparisonItemId = item.comparisonItemId;
  const eventValue = eventSource[field as keyof typeof eventSource];
  const insurerValue = insurerSource[field as keyof typeof insurerSource];
  const discrepancy = item.discrepancies.find(d => d.field === field);
  const eventTypeToCreate = discrepancy?.newEventType;
  const { mutate: createEventFromDifferences } = useCreateEventFromDifferenceMutation();

  const onConfirmCreateEvent = React.useCallback(() => {
    if (connectionId && comparisonItemId && eventTypeToCreate && eventTypeToCreate !== ComparisonItemType.Unknown) {
      const method = 'createEventFromDifferences';
      createEventFromDifferences(
        {
          comparisonId,
          connectionId,
          comparisonItemId,
          newEventType: eventTypeToCreate,
        },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method,
              message: intl.formatMessage({ id: TranslationKeys.baseline_comparison_events_create_successMsg }),
            });
          },
          onError: error => {
            // eslint-disable-next-line no-console
            console.error(error);
            showErrorSnakeBar({ method, message: intl.formatMessage({ id: TranslationKeys.global_errorMsg }) });
          },
        },
      );
    }
  }, [
    connectionId,
    comparisonId,
    createEventFromDifferences,
    comparisonItemId,
    eventTypeToCreate,
    intl,
    showErrorSnakeBar,
    showSuccessSnakeBar,
  ]);

  return (
    <TableRow key={item.eventSource.id}>
      <TableCell sx={{ border: 'none', py: 0.1 }} component="th" scope="row">
        <Typography sx={{ fontWeight: 'medium', color: '#111827' }}>
          {intl.formatMessage({
            id: `baseline.comparison.item.field.${field}`,
          })}
        </Typography>
      </TableCell>
      <TableCell sx={{ border: 'none', py: 0.1 }}>
        <ComparisonEventsListRowDetailField
          field={field}
          value={eventValue}
          discrepanciesFields={discrepanciesFields}
        />
      </TableCell>
      <TableCell sx={{ border: 'none', py: 0.1 }}>
        <ComparisonEventsListRowDetailField field={field} value={insurerValue} discrepanciesFields={[]} />
      </TableCell>

      {item.itemType !== ComparisonItemType.Unknown &&
        eventTypeToCreate !== ComparisonItemType.Unknown &&
        isDifferent &&
        comparisonItemId &&
        insurerValue !== null && (
          <TableCell sx={{ py: 0.1 }} align="left">
            <ActionButton
              size={'small'}
              messageId={intl.formatMessage({
                id: `baseline.comparison.events.create.${eventTypeToCreate?.toLowerCase()}`,
              })}
              onClick={openConfirmCreateEventDialog}
            />
          </TableCell>
        )}

      {showConfirmCreateEventDialog && (
        <ConfirmDialog
          title={
            <FormattedMessage id={TranslationKeys.connection_processes_compareBatch_createEventConfirmDialogTitle} />
          }
          open={showConfirmCreateEventDialog}
          onClose={closeConfirmCreateEventDialog}
          onConfirm={onConfirmCreateEvent}
        >
          <FormattedMessage id={TranslationKeys.connection_processes_compareBatch_createEventConfirmDialogBody} />
        </ConfirmDialog>
      )}
    </TableRow>
  );
};
